import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { variant } from 'styled-system';
import { textStyles } from '../../../theme/config/typography';
import { ButtonKind, ButtonSize } from './enums';
import { StyledButtonProps } from './types';

const sizeStyles = (size: ButtonSize) =>
  size === ButtonSize.Small
    ? css`
        height: 2rem;
        padding: 0 1rem;
        font-size: 0.8125rem;
        border-radius: var(--r-sm);
      `
    : css`
        height: 2.5rem;
        padding: 0 1.25rem;
        font-size: 0.875rem;
        border-radius: var(--r-md);
      `;

const kindStyles = (kind: ButtonKind) => {
  switch (kind) {
    case ButtonKind.Ink:
      return css`
        background-color: var(--brand-ink);
        color: var(--surface);
        box-shadow: 0 1px 3px rgba(9, 15, 56, 0.25);
        &:hover {
          background-color: var(--brand-ink-2);
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(9, 15, 56, 0.3);
        }
        &:active {
          transform: translateY(0);
          background-color: var(--brand-ink-3);
          box-shadow: 0 1px 3px rgba(9, 15, 56, 0.25);
        }
      `;
    case ButtonKind.Outline:
      return css`
        background-color: var(--surface);
        color: var(--ink);
        border: 1px solid var(--line) !important;
        &:hover {
          background-color: var(--brand-green-soft);
          border-color: var(--brand-green) !important;
          color: var(--brand-green);
          transform: translateY(-1px);
        }
        &:active {
          transform: translateY(0);
          background-color: var(--brand-green-soft);
        }
      `;
    case ButtonKind.Ghost:
      return css`
        background-color: transparent;
        color: var(--ink);
        &:hover {
          background-color: var(--paper-2);
        }
        &:active {
          background-color: var(--line);
        }
      `;
    case ButtonKind.Soft:
      return css`
        background-color: var(--paper);
        color: var(--ink);
        border: 1px solid var(--line) !important;
        &:hover {
          background-color: var(--paper-2);
          transform: translateY(-1px);
        }
        &:active {
          transform: translateY(0);
          background-color: var(--line);
        }
      `;
    case ButtonKind.Danger:
      return css`
        background-color: var(--danger);
        color: var(--surface);
        box-shadow: 0 1px 3px rgba(212, 121, 116, 0.25);
        &:hover {
          background-color: var(--danger-ink);
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(212, 121, 116, 0.3);
        }
        &:active {
          transform: translateY(0);
        }
      `;
    default:
      return css`
        background-color: var(--brand-green);
        color: var(--surface);
        box-shadow: 0 1px 3px rgba(118, 177, 89, 0.25);
        &:hover {
          background-color: var(--brand-green-2);
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(118, 177, 89, 0.3);
        }
        &:active {
          transform: translateY(0);
          background-color: var(--brand-green-3);
          box-shadow: 0 1px 3px rgba(118, 177, 89, 0.25);
        }
      `;
  }
};

const ButtonStyles = css<StyledButtonProps>`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
  border: 0;
  font-weight: 600;
  letter-spacing: -0.005em;
  outline: none;
  cursor: pointer;
  text-decoration: none;
  white-space: nowrap;
  transition: background-color 160ms ease, color 160ms ease,
    border-color 160ms ease, box-shadow 160ms ease, transform 160ms ease;

  ${({ size = ButtonSize.Big }) => sizeStyles(size)}
  ${({ kind = ButtonKind.Primary }) => kindStyles(kind)}

  &:disabled,
  &[disabled] {
    pointer-events: none;
    opacity: 0.4;
    box-shadow: none;
    transform: none;
  }

  svg {
    width: 1em;
    height: 1em;
    font-size: 1.25rem;
    fill: currentColor;
    flex-shrink: 0;
  }

  ${variant({ variants: { ...textStyles } })}
`;

export const StyledButton = styled.button<StyledButtonProps>`
  ${({ isFullWidth }) =>
    isFullWidth &&
    css`
      width: 100%;
    `}
  ${ButtonStyles}
`;

export const StyledButtonLink = styled(Link)<StyledButtonProps>`
  ${ButtonStyles}
`;

export const StyledButtonExternalLink = styled.a<StyledButtonProps>`
  ${ButtonStyles}
`;

export const StyledButtonClean = styled.button`
  padding: 0;
  border: 0;
  outline: none;
  background-color: transparent;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  color: inherit;
  &:disabled,
  &[disabled] {
    pointer-events: none;
    opacity: 0.3;
  }
`;
